
import { jitsiLocalStorage } from '@jitsi/js-utils';
import { sha256 } from 'js-sha256';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import SpeechRecognition, {
    useSpeechRecognition
} from 'react-speech-recognition';
import styled from 'styled-components';

import { reduceAllUserVolumeTemporarily } from '../../../base/conference';
import { apiKEY, mtDomain } from '../../../base/config/constants';
import { Dialog, closeDialogs, isAnyDialogOpen } from '../../../base/dialog';
import {
    AnimatedSVG,
    Icon,
    IconHelp,
    IconMicrophone
} from '../../../base/icons';
import { setVoiceCommandString } from '../../../base/mtApi/actions';
import { fetchAxiosApi } from '../../../base/mtApi/functions';
import { getLocalVideoTrack } from '../../../base/tracks';
import logger from '../../../local-recording/logger';
import { checkText } from '../../constants';

import { WebMediaRecorderAdapter } from './WebMediaRecorderAdapter';

const stringSimilarity = require('string-similarity');

/**
 * Custom voice recording class that directly accesses the user's microphone
 * without going through the conference audio system
 */
export class VoiceRecorder {
    /**
     * Creates a new instance of VoiceRecorder.
     * Initializes the media recorder, stream, and recorded chunks storage.
     */
    constructor() {
        this.mediaRecorder = null;
        this.stream = null;
        this.recordedChunks = [];
        this._stopPromise = null;
    }

    /**
     * Starts the voice recording process.
     * @returns {Promise<void>} Resolves when recording starts successfully.
     */
    async start() {
        if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
            return;
        }

        this.stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true
            },
            video: false
        });

        this.recordedChunks = [];

        // Choose a supported mimeType
        const preferred = 'audio/webm;codecs=opus';
        const fallback = 'audio/webm';
        const mimeType = MediaRecorder.isTypeSupported?.(preferred) ? preferred
            : MediaRecorder.isTypeSupported?.(fallback) ? fallback : '';

        this.mediaRecorder = new MediaRecorder(this.stream, mimeType ? { mimeType } : undefined);

        this.mediaRecorder.ondataavailable = event => {
            if (event.data && event.data.size > 0) {
                this.recordedChunks.push(event.data);
            }
        };

        this.mediaRecorder.start(); // sync
        logger?.log?.('Voice recording started with direct microphone access');
    }

    /**
     * Stops the voice recording process.
     * @returns {Promise<Blob>} Resolves with the recorded audio blob.
     */
    async stop() {
        if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
            throw new Error('No active recording');
        }

        if (this._stopPromise) {
            return this._stopPromise;
        } // prevent double stop

        this._stopPromise = new Promise((resolve, reject) => {
            const cleanupAll = () => {
                if (this.stream) {
                    this.stream.getTracks().forEach(t => t.stop());
                    this.stream = null;
                }
                this.mediaRecorder = null;
            };

            let settled = false;

            this.mediaRecorder.onstop = () => {
                try {
                    const type = this.recordedChunks.length
                        ? this.recordedChunks[0].type || 'audio/webm'
                        : 'audio/webm';
                    const audioBlob = new Blob(this.recordedChunks, { type });

                    this.recordedChunks = [];
                    cleanupAll();
                    logger?.log?.('Voice recording stopped');
                    settled = true;
                    resolve(audioBlob);
                } catch (err) {
                    cleanupAll();
                    settled = true;
                    reject(err);
                }
            };

            this.mediaRecorder.onerror = e => {
                if (!settled) {
                    cleanupAll();
                    settled = true;
                    reject(e.error || e);
                }
            };

            // Ensure the final chunk is flushed before stopping
            try {
                this.mediaRecorder.requestData?.();
            } catch (error) { /* empty */ }
            this.mediaRecorder.stop();

            // Safety net in case onstop never fires on some browsers
            setTimeout(() => {
                if (!settled) {
                    try {
                        const type = this.recordedChunks.length
                            ? this.recordedChunks[0].type || 'audio/webm'
                            : 'audio/webm';
                        const audioBlob = new Blob(this.recordedChunks, { type });

                        this.recordedChunks = [];
                        cleanupAll();
                        settled = true;
                        resolve(audioBlob);
                    } catch (err) {
                        cleanupAll();
                        settled = true;
                        reject(err);
                    }
                }
            }, 1500);
        });

        return this._stopPromise.finally(() => {
            this._stopPromise = null;
        });
    }

    /**
     * Cleans up resources used by the recorder.
     */
    cleanup() {
        if (this.stream) {
            this.stream.getTracks().forEach(t => t.stop());
            this.stream = null;
        }
        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
            try {
                this.mediaRecorder.stop();
            } catch (error) {
                // Ignore errors when stopping media recorder
            }
        }
        this.mediaRecorder = null;
        this.recordedChunks = [];
        this._stopPromise = null;
    }
}

// export const checkText: Object<String> = {
//     'DonorBox': 'Open donorbox',
//     'clicknPledge': 'Open click and pledge',
//     'Recording': 'Open recording',
//     'Livestream': 'Open livestream',
//     'WhiteBoard': 'Open whiteboard'
// };

const DIVs = styled.div`
  padding: 10px;
`;

const Ps = styled.p`
  font-size: 15px;
  font-family: "Poppins";
`;

const StyledDiv = styled.div`
    margin-top: 10px;
`;

export const VoiceCommandDialog = () => {
    const {
        transcript,
        listening,
        resetTranscript,
        browserSupportsSpeechRecognition
    } = useSpeechRecognition();
    const adapterRef = useRef(new WebMediaRecorderAdapter());
    const selectedMicId = undefined; // or a deviceId string from enumerateDevices
    const { t } = useTranslation();
    const tooltipRef = useRef(null);
    const [ open, setOpen ] = useState(false);
    const hints = t('voicecommand.hints', {
        lng: 'en',
        returnObjects: true
    });

    const [ change, setChange ] = React.useState('');
    const [ listen, setListen ] = useState(false);
    const { accessToken } = useSelector(state => state['features/base/jwt']);
    const userAccessToken = jitsiLocalStorage.getItem('accessToken');
    const config = useSelector(state => state['features/base/config']);
    const accesTokn = accessToken ?? userAccessToken;
    const [ loader, setLoader ] = React.useState(false);
    const [ record, setRecorder ] = React.useState(false);
    const [ isNotSupported, setNotSupported ] = useState(true);
    const [ resData, setResData ] = useState('');
    const dispatch = useDispatch();
    const localVideo = useSelector(state =>
        getLocalVideoTrack(state['features/base/tracks'])
    );
    const store = useSelector(state => state);
    const isMounted = React.useRef(false);

    // Local-only recording adapter for voice commands
    const [ localRecorder, setLocalRecorder ] = React.useState(null);

    // tempmuted from store
    const tempMuted = useSelector(state => state['features/base/conference'].tempMuted);

    // ★ NEW: Apply ducking
    const tempMuteUsers = React.useCallback(async () => {
        dispatch(reduceAllUserVolumeTemporarily());
    }, [ tempMuted ]);

    // ★ NEW: Remove ducking (restore volumes)
    const unMuteUsers = React.useCallback(() => {
        dispatch(reduceAllUserVolumeTemporarily());

    }, [ tempMuted, listening ]);

    React.useMemo(() => {
        if (
            localVideo
            && localVideo.videoType === 'desktop'
            && isMounted.current === true
        ) {

            if (isAnyDialogOpen(store)) {

                dispatch(closeDialogs(true));
            }
        }
    }, [ localVideo ]);

    useEffect(() => {
        if (!browserSupportsSpeechRecognition && isNotSupported) {
            setNotSupported(!isNotSupported);
        }
        isMounted.current = true;

        return () => {
            if (listening && !isNotSupported) {
                SpeechRecognition.stopListening();

                // unMuteUsers();

                // Restore audio volume on cleanup
                // unduck();
            }

            if (listen && isNotSupported && localRecorder) {
                // Clean up local recording if it's active
                localRecorder.cleanup();
            }

            // unMuteUsers();
        };
    }, [ browserSupportsSpeechRecognition, isNotSupported, listening, listen, localRecorder ]);

    React.useEffect(() => {
        if (!listening && transcript) {
            setListen(false);

            setLoader(true);
            setTimeout(() => {
                const matches = stringSimilarity.findBestMatch(
                    transcript,
                    Object.values(checkText)
                );

                if (matches.bestMatch.rating >= 0.5) {
                    setLoader(false);

                    const key = Object.keys(checkText)[matches?.bestMatchIndex];

                    dispatch(setVoiceCommandString(key));
                    resetTranscript();
                } else {
                    resetTranscript();
                    setResData('The Command does not match what you are looking for');
                    setLoader(false);
                }
            }, 5000);
        }
        if (!listening && !_.isEmpty(change)) {
            const matches = stringSimilarity.findBestMatch(
                change,
                Object.values(checkText)
            );

            if (matches.bestMatch.rating >= 0.5) {
                const key = Object.keys(checkText)[matches?.bestMatchIndex];

                dispatch(setVoiceCommandString(key));
            }

            setListen(false);

            // Restore audio volume when processing change
            // unMuteUsers();
        }
    }, [ change, listening, transcript, dispatch, resetTranscript ]);

    // Helper function to start local-only recording using direct microphone access
    const startLocalRecording = async () => {
        try {
            await adapterRef.current.start(selectedMicId);

            // await recorder.start();
            // setLocalRecorder(recorder);

            // return recorder;
        } catch (error) {
            logger.error('Error starting local recording:', error);
            throw error;
        }
    };

    // Helper function to stop local recording and get the audio data
    const stopLocalRecording = async () => {
        // if (!localRecorder) {
        //     throw new Error('No active local recorder');
        // }

        try {
            // const audioBlob = await localRecorder.stop();
            const { blob } = await adapterRef.current.stop();

            setLocalRecorder(null);

            return blob;
        } catch (error) {
            logger.error('Error stopping local recording:', error);
            setLocalRecorder(null);
            throw error;
        }
    };

    // Update the transcribeBlob function to play the blob after processing
    const transcribeBlob = async audioBlob => {
        setLoader(true);

        try {
            // Convert the audio blob to a file for the API
            const file = new File([ audioBlob ], 'microphone.webm', {
                type: 'audio/webm',
                lastModified: new Date().getTime()
            });

            const moement = moment().seconds(0)
                .milliseconds(0)
                .format('X');

            const mesh256 = sha256(`${apiKEY}:MH:${moement}`);

            const formData = new FormData();

            formData.append('audioFile', file);
            formData.append('client_id', config.MH_CLIENT_ID);
            formData.append('credentials', mesh256);
            formData.append('domain', mtDomain);

            const apiconfig = {
                headers: {
                    'content-type': 'multipart/form-data',
                    Authorization: `Bearer ${accesTokn}`
                }
            };
            const path = 'speechtotext';

            const response = await fetchAxiosApi(path, {
                method: 'post',
                body: formData,
                headers: apiconfig.headers
            });

            if (response && response.transcript) {
                setResData(response.transcript);

                const matches = stringSimilarity.findBestMatch(
                    response.transcript,
                    Object.values(checkText)
                );

                if (matches.bestMatch.rating >= 0.5) {
                    const keys = Object.keys(checkText)[matches?.bestMatchIndex];

                    dispatch(setVoiceCommandString(keys));
                    setLoader(false);

                    return {
                        success: true,
                        transcript: response.data.transcript,
                        command: keys
                    };
                }
            }

            return {
                success: false,
                transcript: response.data?.transcript || ''
            };

        } catch (error) {
            logger.error('Error transcribing audio:', error);
            setLoader(false);

            return {
                success: false,
                error: error.message
            };
        }
    };

    const onListenClick = async e => {
        e.preventDefault();
        if (listening) {
            return;
        }

        if (!isNotSupported) {
            // tempMuteUsers();
            setListen(true);
            setResData('');
            setRecorder(true);

            let totalTimeout;
            let chunkInterval;

            await startLocalRecording();

            // Record for 3 seconds
            setTimeout(async () => {
                try {
                    const audioData = await stopLocalRecording();

                    // chunkCount++;

                    // Process the chunk
                    const result = await transcribeBlob(audioData);

                    // If we got a successful match, stop processing
                    if (result && result.success) {
                        isProcessing = true;
                        clearInterval(chunkInterval);
                        clearTimeout(totalTimeout);
                        setListen(false);
                        setRecorder(false);

                        return;
                    }

                } catch (error) {
                    logger.error('Error processing audio chunk:', error);
                }

                return;
            });
        }

        if (!listening) {
            logger.log('Starting voice command listening...', listening);
            setListen(true);
            resetTranscript();

            // Duck audio before starting speech recognition
            tempMuteUsers();
            SpeechRecognition.startListening();
        }

        setTimeout(() => {
            SpeechRecognition.stopListening();
            setListen(false);
            setChange(transcript);

            // Restore audio volume after stopping
            unMuteUsers();
        }, 10000);

        return;

    };

    useEffect(() => {
        const handleClickOutside = event => {
            if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleClose = e => {
        e.preventDefault();
        dispatch(reduceAllUserVolumeTemporarily());
        dispatch(closeDialogs()); // Close the dialog when the cross button is clicked
    };

    return (
        <Dialog
            disableBlanketClickDismiss = { true }
            disableOverlay = { true }
            onClose = { handleClose } // Handle dialog close
            submitDisabled = { true }
            titleKey = { t('voicecommand.vcLabel') }
            width = 'medium'>
            <DIVs className = 'voice-app'>
                <StyledDiv
                    className = 'help-tip'
                    innerRef = { tooltipRef }
                    onClick = { () => setOpen(prev => !prev) }>
                    <Icon
                        alt = 'help'
                        size = { 16 }
                        src = { IconHelp } />
                    {open && (
                        <ol className = 'help-tip-list'>
                            {Object.entries(hints).map(([ , value ], idx) =>
                                <li key = { idx }>{`${value}`}</li>
                            )}
                        </ol>)
                    }
                </StyledDiv>
                <h2 className = 'voice-command-header'>{t('voicecommand.voiceCommandForMeethour')}</h2>
                <h3>{t('voicecommand.clickOnMic')}</h3>
                <button
                    className = 'btn-mic mb-4 mt-4'
                    id = 'speech'
                    onClick = { onListenClick }>
                    {listen || listening ? <div className = 'pulse-ring' /> : ''}
                    <Icon
                        alt = 'microphone'
                        size = { 50 }
                        src = { loader ? AnimatedSVG : IconMicrophone } />
                </button>
                {!isNotSupported && record
                    && _.isEmpty(resData)
                    && <h4>{t('voicecommand.pleaseWaitWeAreRecording')}</h4>
                }
                <Ps>{transcript || resData}</Ps>
                <ul id = 'playlist' />
            </DIVs>
        </Dialog>
    );
};
